// src/components/legal/PrivacyPolicy.jsx
// Full-screen modal overlay for the Privacy Policy.
// Usage: <PrivacyPolicy onClose={() => setShow(false)} />

export default function PrivacyPolicy({ onClose }) {
  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()} role="presentation">
      <div style={styles.sheet} role="dialog" aria-modal="true" aria-label="Privacy Policy">
        <div style={styles.header}>
          <span style={styles.title}>Privacy Policy</span>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div style={styles.body}>
          <p style={styles.meta}>Last updated: March 2026</p>

          <Section title="1. Who We Are">
            <p>Orbit ("we", "our", "us") is a personal scheduling and relationship management application. This Privacy Policy explains how we collect, use, and protect your information when you use the Orbit web application and related services.</p>
          </Section>

          <Section title="2. Information We Collect">
            <p><strong>Account information.</strong> When you register, we collect your email address and a hashed password managed by Supabase Auth.</p>
            <p><strong>Profile information.</strong> You may optionally provide a display name, phone number, birthday, and profile photo.</p>
            <p><strong>Calendar credentials.</strong> To connect third-party calendars (Google, Microsoft Outlook, Apple iCloud, Yahoo Calendar, CalDAV servers), we store OAuth access/refresh tokens or, for CalDAV-based providers, encrypted app-specific passwords. These credentials are stored encrypted at rest and are used solely to fetch your calendar events on your behalf.</p>
            <p><strong>Calendar event data.</strong> We retrieve event titles, dates, and times from your connected calendars to compute availability. We do not permanently store the content of individual calendar events — event data is fetched on demand and held only in memory during a session.</p>
            <p><strong>People and relationship data.</strong> You may add contacts with names, relationship types, birthdays, anniversaries, and custom tags. This data is stored in our database and is visible only to you.</p>
            <p><strong>Scheduling proposals.</strong> When you send or receive a scheduling proposal through Orbit, the proposed date, time, and optional message are stored until the proposal is accepted, declined, or cancelled.</p>
            <p><strong>Usage and error data.</strong> We collect anonymised error reports and performance traces via Sentry to help us diagnose bugs. These reports do not include calendar event contents or message bodies.</p>
          </Section>

          <Section title="3. How We Use Your Information">
            <ul>
              <li>To provide the core features of the application (calendar aggregation, availability heatmaps, scheduling, people management).</li>
              <li>To send notifications you have opted into (email or SMS summaries, scheduling alerts).</li>
              <li>To diagnose and fix technical errors.</li>
              <li>To comply with legal obligations.</li>
            </ul>
            <p>We do not sell your personal data. We do not use your data for advertising.</p>
          </Section>

          <Section title="4. Third-Party Services">
            <p>Orbit integrates with the following third-party services to function:</p>
            <ul>
              <li><strong>Supabase</strong> — database and authentication (EU/US data centres).</li>
              <li><strong>Google Calendar API</strong> — calendar events for connected Google accounts.</li>
              <li><strong>Microsoft Graph API</strong> — calendar events for connected Outlook/Microsoft accounts.</li>
              <li><strong>Apple CalDAV / Yahoo CalDAV</strong> — calendar events fetched using encrypted stored credentials.</li>
              <li><strong>Resend</strong> — transactional email delivery.</li>
              <li><strong>Twilio</strong> — SMS notification delivery (if enabled).</li>
              <li><strong>Sentry</strong> — anonymised error and performance monitoring.</li>
            </ul>
            <p>Each of these providers has its own privacy policy governing how they handle data transmitted to them.</p>
          </Section>

          <Section title="5. Data Retention">
            <p>Your account data is retained until you delete your account. Calendar credentials are deleted when you disconnect a calendar integration. Scheduling proposals are retained until cancelled or until you delete your account.</p>
          </Section>

          <Section title="6. Your Rights">
            <p>Depending on your jurisdiction, you may have the right to access, correct, export, or delete your personal data. To exercise these rights, contact us at the address below. We will respond within 30 days.</p>
          </Section>

          <Section title="7. Data Security">
            <p>We use industry-standard measures including TLS in transit and AES-256-GCM encryption at rest for stored credentials. OAuth tokens are stored with row-level security policies enforced by Supabase, ensuring only you can access your own data.</p>
          </Section>

          <Section title="8. Children">
            <p>Orbit is not directed at children under 13. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us so we can delete it.</p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by updating the "Last updated" date above and, where required, by email.</p>
          </Section>

          <Section title="10. Contact">
            <p>Questions about this Privacy Policy can be sent to: <strong>privacy@orbitapp.io</strong></p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: "#4A3020", marginBottom: 8, marginTop: 0 }}>{title}</h3>
      <div style={{ fontSize: 13, color: "#5A4535", lineHeight: 1.65 }}>{children}</div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 9999,
    background: "rgba(30,16,8,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 16,
    paddingTop: "max(16px, env(safe-area-inset-top, 16px))",
    overflowY: "auto",
  },
  sheet: {
    width: "100%", maxWidth: 640,
    background: "#FFFCF8",
    border: "1px solid #E4D8C8",
    borderRadius: 18,
    boxShadow: "0 16px 64px rgba(60,40,20,.2)",
    maxHeight: "90vh",
    display: "flex", flexDirection: "column",
    fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: "1px solid #E4D8C8",
    background: "#FDF8F2",
    borderRadius: "18px 18px 0 0",
    flexShrink: 0,
  },
  title: {
    fontSize: 17, fontFamily: "'Crimson Pro', serif", fontWeight: 600, color: "#1E1008",
  },
  closeBtn: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 16, color: "#A08870", padding: "4px 8px", borderRadius: 8,
  },
  body: {
    overflowY: "auto",
    padding: "20px 24px 28px",
  },
  meta: {
    fontSize: 12, color: "#A08870", marginBottom: 20, marginTop: 0,
  },
};
